import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Button, Paper, LinearProgress, CircularProgress, Alert } from '@mui/material';
import { Coffee, Celebration, Apple, PhoneAndroid } from '@mui/icons-material';
import loyaltyService, { StampInfo, StampResult } from '../../services/loyaltyService';
import { useAuth } from '../../contexts/AuthContext';
import { colors } from '../../theme/theme';
import AppSnackbar from '../../components/shared/AppSnackbar';

export default function StampLandingPage() {
  const { venueToken } = useParams<{ venueToken: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [venueInfo, setVenueInfo] = useState<StampInfo | null>(null);
  const [stampResult, setStampResult] = useState<StampResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [stamping, setStamping] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'success' | 'info' | 'warning' }>(
    { open: false, message: '', severity: 'info' }
  );

  // Load venue info (public, no auth needed)
  useEffect(() => {
    if (!venueToken) return;
    loyaltyService.getStampInfo(venueToken)
      .then(info => { setVenueInfo(info); setLoading(false); })
      .catch(() => { setError('Invalid QR code'); setLoading(false); });
  }, [venueToken]);

  // Auto-stamp if already logged in
  /* eslint-disable react-hooks/exhaustive-deps -- intentional one-shot stamp when auth + venue resolve; handleStamp/stampResult/error are read for guards only. */
  useEffect(() => {
    if (isAuthenticated && venueInfo && !stampResult && !error && venueToken) {
      handleStamp();
    }
  }, [isAuthenticated, venueInfo]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleStamp = async () => {
    if (!venueToken) return;
    setStamping(true);
    setError('');
    try {
      const result = await loyaltyService.stamp(venueToken);
      setStampResult(result);
    } catch (e: any) {
      console.warn('[StampLandingPage] stamp failed', e);
      setError(e.message || 'Failed to record stamp');
    } finally {
      setStamping(false);
    }
  };

  if (loading) return <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: colors.primary.main }} /></Container>;

  if (error && !venueInfo) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2, color: colors.error.main }}>Invalid QR Code</Typography>
        <Typography color="text.secondary">Make sure you're scanning a CoffeeMates partner QR code.</Typography>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 3, bgcolor: colors.primary.main }}>Go to CoffeeMates</Button>
      </Container>
    );
  }

  // Not logged in — show enrollment prompt
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {venueInfo?.venue_photo && (
            <Box component="img" src={venueInfo.venue_photo} alt={venueInfo.venue_name}
              sx={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 3, mb: 3 }} />
          )}
          <Coffee sx={{ fontSize: 48, color: colors.primary.main, mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 1 }}>
            Welcome to {venueInfo?.venue_name}!
          </Typography>
          <Typography variant="h6" sx={{ color: colors.neutral[600], mb: 3 }}>
            Get a <strong>FREE {venueInfo?.reward_description}</strong> after {venueInfo?.stamps_required} stamps.
            Your first stamp is on us!
          </Typography>
        </Box>

        <Paper sx={{ p: 3, borderRadius: 3, bgcolor: colors.primary.subtle }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
            Create a free account to save your stamps
          </Typography>
          <Button fullWidth variant="contained" size="large"
            onClick={() => navigate(`/login?redirect=/stamp/${venueToken}`)}
            sx={{ py: 1.5, mb: 1.5, bgcolor: colors.primary.main, '&:hover': { bgcolor: colors.primary.dark }, borderRadius: 2 }}>
            Sign Up with Phone
          </Button>
          <Button fullWidth variant="outlined" size="large"
            onClick={() => navigate(`/login?redirect=/stamp/${venueToken}&method=email`)}
            sx={{ py: 1.5, borderRadius: 2 }}>
            Sign Up with Email
          </Button>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: colors.neutral[500] }}>
            Free, no password needed. Takes 10 seconds.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Stamp result
  if (stampResult) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center' }}>
        {stampResult.reward_earned ? (
          <>
            <Celebration sx={{ fontSize: 64, color: colors.accent.main, mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 1 }}>
              🎉 FREE {venueInfo?.reward_description?.toUpperCase()}!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Show this to your barista!
            </Typography>
            {stampResult.reward && (
              <Paper sx={{ p: 3, bgcolor: colors.accent.subtle, border: `3px solid ${colors.accent.main}`, borderRadius: 3, mb: 3 }}>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
                  {stampResult.reward.qr_token.substring(0, 8).toUpperCase()}
                </Typography>
                <Typography variant="caption" color="text.secondary">Reward code — show to staff</Typography>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Coffee sx={{ fontSize: 64, color: colors.primary.main, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 1 }}>
              {stampResult.message}
            </Typography>
            {stampResult.is_new_card && (
              <Typography variant="body1" sx={{ color: colors.success.main, mb: 1 }}>
                Welcome, {user?.first_name}! Your loyalty card has been created.
              </Typography>
            )}
          </>
        )}

        {/* Progress bar */}
        <Box sx={{ my: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{stampResult.stamps_count} / {stampResult.stamps_required}</Typography>
            <Typography variant="body2" color="text.secondary">
              {stampResult.stamps_required - stampResult.stamps_count} more to go!
            </Typography>
          </Box>
          <LinearProgress variant="determinate"
            value={(stampResult.stamps_count / stampResult.stamps_required) * 100}
            sx={{ height: 12, borderRadius: 6, bgcolor: colors.neutral[200],
              '& .MuiLinearProgress-bar': { bgcolor: colors.primary.main, borderRadius: 6 } }} />
          {/* Stamp dots */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
            {Array.from({ length: stampResult.stamps_required }).map((_, i) => (
              <Box key={i} sx={{
                width: 28, height: 28, borderRadius: '50%',
                bgcolor: i < stampResult.stamps_count ? colors.primary.main : colors.neutral[200],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', color: i < stampResult.stamps_count ? 'white' : colors.neutral[400],
              }}>
                {i < stampResult.stamps_count ? '☕' : i + 1}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Wallet buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
          <Button variant="outlined" startIcon={<Apple />} size="small"
            onClick={() => setSnackbar({ open: true, message: 'Apple Wallet coming soon — set up your Apple Developer certificate.', severity: 'info' })}>
            Add to Apple Wallet
          </Button>
          <Button variant="outlined" startIcon={<PhoneAndroid />} size="small"
            onClick={() => setSnackbar({ open: true, message: 'Google Wallet coming soon — set up your Google Cloud service account.', severity: 'info' })}>
            Add to Google Wallet
          </Button>
        </Box>

        {/* Cross-sell events */}
        <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: colors.neutral[50] }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Did you know? CoffeeMates hosts weekly meetups at {stampResult.venue_name}!
          </Typography>
          <Button variant="text" size="small" onClick={() => navigate('/events')}
            sx={{ color: colors.primary.main }}>
            See This Week's Events →
          </Button>
        </Paper>

        <Button fullWidth variant="contained" onClick={() => navigate('/loyalty')}
          sx={{ mt: 3, py: 1.5, bgcolor: colors.primary.main }}>
          View My Loyalty Cards
        </Button>

        <AppSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        />
      </Container>
    );
  }

  // Stamping in progress
  if (stamping) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ color: colors.primary.main, mb: 2 }} />
        <Typography variant="h6">Recording your stamp...</Typography>
      </Container>
    );
  }

  // Error after stamp attempt
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center' }}>
        <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/loyalty')}
          sx={{ bgcolor: colors.primary.main }}>View My Cards</Button>
      </Container>
    );
  }

  return null;
}
